import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/auth'
import { usePermissions } from '@/hooks/use-permissions'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { canAccessPage, type RoleName } from '@/lib/utils/role-access'
import { Card, CardContent } from '@/components/ui/card'

function AccessDeniedFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center text-sm text-muted-foreground">
          You do not have permission to access this page.
        </CardContent>
      </Card>
    </div>
  )
}

interface ProtectedRouteProps {
  children?: React.ReactNode
  requiredRole?: string
  requiredPermission?: string
  fallbackPath?: string
}

/**
 * ProtectedRoute - Route guard for authenticated routes
 * 
 * Uses React Router's Navigate component (declarative) instead of useEffect + navigate (imperative)
 * This prevents redirect loops by letting React Router handle navigation timing
 * 
 * Usage (as Route element):
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * 
 * Usage (as wrapper - backward compatible):
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/dashboard'
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, isInitialized } = useAuthStore()
  const { can } = usePermissions()
  const location = useLocation()

  // Show loading spinner while checking authentication/permissions
  if (isLoading || !isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Redirect unauthenticated users to signin
  if (!user || !isAuthenticated) {
    return <Navigate to="/signin" replace />
  }

  const userRole = user.role?.name as RoleName | undefined
  const safeFallbackPath = fallbackPath || '/dashboard'

  // Enforce path-based RBAC for all protected routes.
  if (!canAccessPage(location.pathname, userRole)) {
    if (location.pathname === safeFallbackPath) {
      return <AccessDeniedFallback />
    }
    return <Navigate to={safeFallbackPath} replace />
  }

  // Check role requirement
  if (requiredRole && userRole !== requiredRole) {
    if (location.pathname === safeFallbackPath) {
      return <AccessDeniedFallback />
    }
    return <Navigate to={safeFallbackPath} replace />
  }

  // Check permission requirement
  if (requiredPermission && !can(requiredPermission)) {
    if (location.pathname === safeFallbackPath) {
      return <AccessDeniedFallback />
    }
    return <Navigate to={safeFallbackPath} replace />
  }

  // If used as Route element (with Outlet pattern)
  if (!children) {
    return <Outlet />
  }

  // If used as wrapper (backward compatible)
  return <>{children}</>
}
